import React, { Component } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { formatNumber } from "@formant/core/common/formatNumber";
import { duration } from "@formant/core/model/duration";
import { Timeout } from "@formant/core/model/Timeout";

import { IValueTooltip } from "../../model/IValueTooltip";
import { appStore } from "../../stores";
import { formatTime } from "../../utils/formatTime";
import { DeviceName } from "../common/DeviceName";
import { PieChart } from "../common/PieChart";
import { Tags } from "../common/Tags";
import * as styles from "./ValueTooltip.scss";

interface IValueTooltipProps {
    clickable?: boolean;
}

@observer
export class ValueTooltip extends Component<IValueTooltipProps> {
    private ref?: HTMLDivElement;
    private tooltip?: IValueTooltip;
    private timeoutId?: Timeout;

    private hideTooltip = () => {
        appStore.valueTooltip = undefined;
        this.tooltip = undefined;
    };

    // keep visible while hover
    private onMouseEnter = () => {
        clearTimeout(this.timeoutId);
    };
    private onMouseLeave = () => {
        this.delayHide();
    };

    private delayHide = () => {
        this.timeoutId = setTimeout(this.hideTooltip, 5 * duration.second);
    };

    public componentWillUnmount() {
        clearTimeout(this.timeoutId);
    }

    public render() {
        this.tooltip = appStore.valueTooltip || this.tooltip;

        if (!this.tooltip) {
            return null;
        }

        if (!this.timeoutId) {
            this.delayHide();
        }

        const visible = !!appStore.valueTooltip;
        const { clickable } = this.props;
        const { tooltip, ref } = this;
        const { y, time, value, deviceId, tags, color, cpu, memory, disk } =
            tooltip;

        const x =
            ref && tooltip.x + ref.clientWidth > window.innerWidth
                ? tooltip.x - ref.clientWidth
                : tooltip.x;

        const approximate =
            (value?.type === "numeric" && value.minimum !== value.maximum) ||
            value?.type === "location";

        return (
            <div
                ref={_ => (this.ref = _ || undefined)}
                className={classnames(
                    styles["value-tooltip"],
                    visible && styles.visible,
                    clickable && styles.clickable,
                )}
                style={{
                    left: x < 0 ? 0 : x,
                    top: y,
                }}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}
            >
                <div
                    className={styles.color}
                    style={{ backgroundColor: color }}
                />
                {value?.type === "location-overview" && (
                    <DeviceName
                        className={styles["device-name-overview"]}
                        id={deviceId}
                        showIcon={false}
                    />
                )}

                <div className={styles.info}>
                    {value !== undefined && (
                        <div className={styles.value}>
                            {value.type === "numeric" &&
                                formatNumber(value.average)}

                            {value.type === "boolean" &&
                                (value.value ? "true" : "false")}
                        </div>
                    )}
                    {value !== undefined && approximate && (
                        <div className={styles.range}>
                            {value.type === "numeric" &&
                                `(${formatNumber(
                                    value.minimum,
                                    true,
                                )}—${formatNumber(value.maximum, true)})`}
                        </div>
                    )}
                    {value !== undefined && value.type === "location" && (
                        <div className={styles.value}>
                            {`${value.latitude.toFixed(
                                6,
                            )}, ${value.longitude.toFixed(6)}`}
                        </div>
                    )}
                </div>
                {value?.type === "location-overview" ? (
                    <div className={styles.info}>
                        {cpu !== undefined && (
                            <div className={styles.stat}>
                                <div className={styles.statLabel}>CPU</div>
                                <div className={styles.statValue}>
                                    <span>{formatNumber(cpu, true, 0)}%</span>
                                    <PieChart
                                        value={cpu}
                                        min={0}
                                        max={100}
                                        size={20}
                                    />
                                </div>
                            </div>
                        )}
                        {disk !== undefined && (
                            <div className={styles.stat}>
                                <div className={styles.statLabel}>Disk</div>
                                <div className={styles.statValue}>
                                    <span>{formatNumber(disk, true, 0)}%</span>
                                    <PieChart
                                        value={disk}
                                        min={0}
                                        max={100}
                                        size={20}
                                    />
                                </div>
                            </div>
                        )}
                        {memory !== undefined && (
                            <div className={styles.stat}>
                                <div className={styles.statLabel}>Memory</div>
                                <div className={styles.statValue}>
                                    <span>
                                        {formatNumber(memory, true, 0)}%
                                    </span>
                                    <PieChart
                                        value={memory}
                                        min={0}
                                        max={100}
                                        size={20}
                                    />
                                </div>
                            </div>
                        )}
                        {value !== undefined && (
                            <div className={styles.stat}>
                                <div className={styles.statLabel}>Location</div>
                                <div className={styles.locationValue}>
                                    <span>{value.latitude.toFixed(6)}, </span>
                                    <span>{value.longitude.toFixed(6)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {time !== undefined && (
                            <div className={styles.time}>
                                {approximate && "~"}
                                {formatTime(time, {
                                    milliseconds: !approximate,
                                })}
                            </div>
                        )}
                        <DeviceName
                            className={styles["device-name"]}
                            id={deviceId}
                        />
                        <Tags tags={tags} />
                    </>
                )}
            </div>
        );
    }
}
